import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import CategoriesSortableItem from './CategoriesSortableItem';
import { useEffect, useRef, useState } from 'react';
import shopSelectors from 'services/shop/selectors';
import { IProductCategoryItem } from 'services/shop/types/product';
import { clone, findIndex, pullAt } from 'lodash';
import styled from 'styled-components';
import AddOrUpdateCategoryModal, {
  IModalAddOrUpdateCategoryRef,
} from './Modals/AddOrUpdateCategoryModal';
import AddOrUpdateSupplierModal, {
  IModalAddOrUpdateSupplierRef,
} from 'features/settingService/pages/SuppliersSetting/components/Modals/AddOrUpdateSupplierModal';
import ModalConfirm, { useModalConfirmRef } from 'components/Modal/ModalConfirm';
import settingApis from 'features/settingService/services/apis';
import toast from 'utils/toast';
import shopActions from 'services/shop/actions';
import { useAppDispatch } from 'store/hooks';
import ArrangeCategoryModal from './Modals/ArrangeCategoryModal';

interface Props {
  value?: string;
  onChange?: (value: string) => void;
}

const CategoriesDragAndDrop = ({ value, onChange }: Props) => {
  const dispatch = useAppDispatch();
  const categoriesSaga = shopSelectors.data.productCategories();
  const [categoriesSagaRender, setCategoriesSagaRender] = useState<IProductCategoryItem[]>([]);

  const addOrUpdateCategoryRef = useRef<IModalAddOrUpdateCategoryRef>(null);
  const addOrUpdateSupplierRef = useRef<IModalAddOrUpdateSupplierRef>(null);
  const confirmModalRef = useModalConfirmRef();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 50 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const moveItemById = (arr: IProductCategoryItem[], fromId: string, toId: string) => {
    const fromIndex = findIndex(arr, { id: fromId });
    const toIndex = findIndex(arr, { id: toId });

    if (fromIndex === -1 || toIndex === -1) return arr;

    const newArr = clone(arr);
    const [moved] = pullAt(newArr, fromIndex);
    newArr.splice(toIndex, 0, moved);
    return newArr;
  };

  useEffect(() => {
    setCategoriesSagaRender(categoriesSaga);
  }, [categoriesSaga]);

  const onDeleteCategory = async (id: string) => {
    try {
      await settingApis.categoryDelete(id);
      toast.success('Delete category successfully');
      dispatch(shopActions.get.productCategories.fetch());
    } catch (error) {}
  };

  const handleAddCategory = () => {
    addOrUpdateCategoryRef.current?.open();
  };
  const handleUpdateCategory = (data: IProductCategoryItem) => {
    addOrUpdateCategoryRef.current?.open(data);
  };
  const handleDelete = (data: IProductCategoryItem) => {
    confirmModalRef.current?.open(`Do you want to remove ${data.categoryName}?`, () =>
      onDeleteCategory(data.id)
    );
  };

  const handleAddSupplier = () => {
    addOrUpdateSupplierRef.current?.open();
  };

  const handleSortCategories = async (categories: IProductCategoryItem[]) => {
    try {
      const res = await settingApis.sortCategories(categories.map((o) => o.id));
      if (res?.data?.data) {
        toast.success('Arrange categories successfully');
        dispatch(shopActions.get.productCategories.fetch());
      }
    } catch (error) {
      toast.error('Arrange categories failed');
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (!over) return;
        const newCategoriesSaga = moveItemById(
          categoriesSagaRender,
          active.id as string,
          over.id as string
        );
        setCategoriesSagaRender(newCategoriesSaga);
        handleSortCategories(newCategoriesSaga);
      }}
    >
      <SortableContext items={categoriesSagaRender}>
        <CategoriesDragAndDropListStyled>
          {categoriesSagaRender.map((item) => (
            <CategoriesSortableItem
              key={item.id}
              data={item}
              isActive={value === item.id}
              onChange={onChange}
              onUpdate={handleUpdateCategory}
              onDelete={handleDelete}
            />
          ))}
        </CategoriesDragAndDropListStyled>
        <ButtonGroup>
          <button className='btn-container add-category' onClick={handleAddCategory}>
            <IcAddCategory />
            Add Category
          </button>
          {/* <button className='btn-container add-supplier' onClick={handleAddSupplier}>
            <IcAddSupplier />
            Add Supplier
          </button> */}
          <ArrangeCategoryModal data={categoriesSagaRender} />
        </ButtonGroup>
        <AddOrUpdateCategoryModal ref={addOrUpdateCategoryRef} />
        <AddOrUpdateSupplierModal ref={addOrUpdateSupplierRef} />
        <ModalConfirm ref={confirmModalRef} />
      </SortableContext>
    </DndContext>
  );
};

export default CategoriesDragAndDrop;

const CategoriesDragAndDropListStyled = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 1rem;
  overflow-y: auto;
  max-height: calc(100vh - 68px);
  padding-bottom: 150px;
`;

const ButtonGroup = styled.div`
  padding: 8px;
  gap: 8px;
  height: 150px;
  position: absolute;
  background-color: #fff;
  z-index: 9;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .btn-container {
    display: flex;
    padding: 16px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 2px;
    flex: 1 0 0;
    align-self: stretch;
    height: 100%;
    box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3),
      0 -3px 0 0 #d6d6e7 inset;
    color: #1d2129;
    text-align: center;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }
  .add-category {
    background: #c6e9ef;
  }
  .add-supplier {
    background: #effffa;
  }
`;

const IcAddCategory = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='80' height='81' viewBox='0 0 80 81' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M40 15.75C41.3807 15.75 42.5 16.8693 42.5 18.25V63.25C42.5 64.6307 41.3807 65.75 40 65.75C38.6193 65.75 37.5 64.6307 37.5 63.25V18.25C37.5 16.8693 38.6193 15.75 40 15.75Z'
      fill='#232F3E'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M15 40.75C15 39.3693 16.1193 38.25 17.5 38.25H62.5C63.8807 38.25 65 39.3693 65 40.75C65 42.1307 63.8807 43.25 62.5 43.25H17.5C16.1193 43.25 15 42.1307 15 40.75Z'
      fill='#232F3E'
    />
    <path
      d='M40 14.083C42.3012 14.083 44.167 15.9488 44.167 18.25V36.583H62.5C64.8012 36.583 66.667 38.4488 66.667 40.75C66.667 43.0512 64.8012 44.917 62.5 44.917H44.167V63.25C44.167 65.5512 42.3012 67.417 40 67.417C37.6988 67.417 35.833 65.5512 35.833 63.25V44.917H17.5C15.1988 44.917 13.333 43.0512 13.333 40.75C13.333 38.4488 15.1988 36.583 17.5 36.583H35.833V18.25C35.833 15.9488 37.6988 14.083 40 14.083ZM39.167 44.917V63.25C39.167 63.7102 39.5398 64.083 40 64.083C40.4602 64.083 40.833 63.7102 40.833 63.25V44.917H39.167ZM44.167 41.583H62.5C62.9602 41.583 63.333 41.2102 63.333 40.75C63.333 40.2898 62.9602 39.917 62.5 39.917H44.167V41.583ZM39.167 41.583H40.833V39.917H39.167V41.583ZM17.5 39.917C17.0398 39.917 16.667 40.2898 16.667 40.75C16.667 41.2102 17.0398 41.583 17.5 41.583H35.833V39.917H17.5ZM40 17.417C39.5398 17.417 39.167 17.7898 39.167 18.25V36.583H40.833V18.25C40.833 17.7898 40.4602 17.417 40 17.417Z'
      fill='#232F3E'
    />
  </svg>
);

const IcAddSupplier = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='80' height='81' viewBox='0 0 80 81' fill='none'>
    <g clipPath='url(#clip0_20512_16304)'>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M2.1875 74.9551C0.892946 74.9551 -0.15625 73.9059 -0.15625 72.6114V58.5934C-0.15625 51.4583 4.55567 45.3914 11.4777 43.6177L19.5819 41.5407C20.5463 41.2947 21.5241 41.689 22.0661 42.4562L27.2144 49.2409L32.3542 42.4685C32.9359 41.7012 33.9002 41.3955 34.7785 41.6146L42.8822 43.6434C44.076 43.9431 45.2027 44.3691 46.2501 44.9056V37.0015C46.2501 35.707 47.2993 34.6578 48.5938 34.6578H77.5002C78.7941 34.6578 79.8439 35.707 79.8439 37.0015V72.6236C79.8439 73.9181 78.7941 74.9673 77.5002 74.9673C52.3957 74.9673 27.2919 74.9551 2.1875 74.9551ZM27.2144 6.53271C18.4992 6.53271 11.4331 13.5988 11.4331 22.314C11.4331 31.0292 18.4992 38.0953 27.2144 38.0953C35.9296 38.0953 42.9957 31.0292 42.9957 22.314C42.9957 13.5988 35.9296 6.53271 27.2144 6.53271ZM35.0593 14.4691C39.3916 18.8014 39.3916 25.8266 35.0593 30.1589C30.727 34.4905 23.7024 34.4905 19.3702 30.1589C15.0379 25.8266 15.0379 18.8014 19.3702 14.4691C23.7024 10.1375 30.727 10.1375 35.0593 14.4691ZM70.218 39.3453V47.0742C70.218 48.7307 68.4498 50.0844 66.6322 49.0615L63.0617 47.267L59.2927 49.1616C57.7235 49.9483 55.9132 48.7722 55.9144 47.0742L55.9053 39.3453H50.9376V70.2798H75.1564V39.3453H70.218ZM65.5305 39.6578H60.5928V43.2845C63.1764 41.9857 62.9219 41.9735 65.5305 43.2845V39.6578ZM46.2501 50.5111C44.9671 49.4015 43.4211 48.6049 41.7469 48.1844L35.1343 46.523C34.2731 47.6577 29.1968 54.5247 28.6243 54.9581C27.5983 55.7369 26.1347 55.5361 25.3559 54.5095L19.2377 46.4473L12.6313 48.1405C7.78383 49.3825 4.53126 53.5799 4.53126 58.5934V70.2676H46.2501V50.5111Z'
        fill='#232F3E'
      />
    </g>
    <defs>
      <clipPath id='clip0_20512_16304'>
        <rect width='80' height='80' fill='white' transform='translate(0 0.75)' />
      </clipPath>
    </defs>
  </svg>
);
